/**
 * LODEN — component-product-gallery.js
 * <product-gallery>  Media switching, thumbnails, keyboard navigation, zoom.
 * <sticky-atc>       Sticky add-to-cart bar.
 * <share-buttons>    Native share sheet with a copy-link fallback.
 *
 * The gallery is server-rendered: every image is already in the DOM. This file
 * only changes which one is active, so there is no loading state when
 * switching and no client-side templating of media.
 */
(function () {
  'use strict';

  const { Element, a11y, motion, utils } = window.Aurelia;

  /* =====================================================================
     Gallery
     ===================================================================== */
  class ProductGallery extends Element {
    mount() {
      this.items = this.$$('[data-gallery-item]');
      if (this.items.length === 0) return;

      this.thumbs = this.$$('[data-gallery-thumb]');
      this.list = this.$('[data-gallery-list]');
      this.counter = this.$('[data-gallery-index]');
      this.index = Math.max(
        this.items.findIndex((item) => item.classList.contains('is-active')),
        0
      );

      this.thumbs.forEach((thumb) => {
        this.listen(thumb, 'click', () => this.selectByMediaId(thumb.dataset.mediaId));
      });

      const previous = this.$('[data-gallery-prev]');
      const next = this.$('[data-gallery-next]');
      if (previous) this.listen(previous, 'click', () => this.step(-1));
      if (next) this.listen(next, 'click', () => this.step(1));

      // Left/right arrows move through media when focus is inside the gallery.
      this.listen(this, 'keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      });

      // Selecting a variant with its own image should show that image.
      this.subscribe('variant:media', ({ mediaId, sectionId }) => {
        if (sectionId && sectionId !== this.dataset.sectionId) return;
        this.selectByMediaId(String(mediaId));
      });

      if (this.dataset.zoom === 'true') {
        this.$$('[data-gallery-zoom]').forEach((button) => {
          this.listen(button, 'click', () => this.openLightbox());
        });
      }

      // On mobile the stage is a native scroller; keep the counter honest.
      if (this.list) {
        this.onScroll = utils.rafThrottle(() => this.syncFromScroll());
        this.listen(this.list, 'scroll', this.onScroll, { passive: true });
      }

      this.select(this.index, { scroll: false });
    }

    unmount() {
      if (this.lightbox) this.closeLightbox();
    }

    selectByMediaId(mediaId) {
      const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
      if (index > -1) this.select(index);
    }

    step(direction) {
      const total = this.items.length;
      this.select((this.index + direction + total) % total);
    }

    select(index, { scroll = true } = {}) {
      this.index = index;

      this.items.forEach((item, position) => {
        const isActive = position === index;
        item.classList.toggle('is-active', isActive);

        // Pause any video that scrolls out of the active slot rather than
        // leaving it playing behind another image.
        const video = item.querySelector('video');
        if (video && !isActive) video.pause();
      });

      this.thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.mediaId === this.items[index].dataset.mediaId;
        thumb.classList.toggle('is-active', isActive);
        // Presence, not "false" — matching how the server renders it, so the
        // markup does not change shape the first time a thumbnail is clicked.
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
        if (isActive) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });

      if (this.counter) this.counter.textContent = index + 1;

      if (scroll && this.list && this.isScroller()) {
        this.items[index].scrollIntoView({
          behavior: motion.reduced ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    }

    isScroller() {
      return getComputedStyle(this.list).overflowX !== 'visible';
    }

    syncFromScroll() {
      if (!this.isScroller()) return;
      const { scrollLeft, clientWidth } = this.list;
      const index = Math.round(Math.abs(scrollLeft) / clientWidth);
      if (index === this.index || !this.items[index]) return;
      this.select(index, { scroll: false });
    }

    /* --- Lightbox ------------------------------------------------------
       Built on demand and destroyed on close: a product page with twelve
       full-resolution images should not carry a second copy of them in the
       DOM on the chance the customer zooms. */
    openLightbox() {
      if (this.lightbox) return;

      const dialog = document.createElement('div');
      dialog.className = 'lightbox';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-label', this.dataset.lightboxLabel || 'Product images');

      const figure = document.createElement('div');
      figure.className = 'lightbox__stage';

      this.items.forEach((item, position) => {
        const source = item.querySelector('img');
        if (!source) return;
        const clone = document.createElement('img');
        clone.className = 'lightbox__image';
        // `srcset` is preserved so the browser still picks a sensible size for
        // the viewport instead of always fetching the largest candidate.
        clone.src = source.src;
        clone.srcset = source.srcset;
        clone.sizes = '95vw';
        clone.alt = source.alt;
        clone.loading = position === this.index ? 'eager' : 'lazy';
        clone.decoding = 'async';
        if (position === this.index) clone.classList.add('is-active');
        figure.appendChild(clone);
      });

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'button button--icon lightbox__close';
      close.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      close.setAttribute('aria-label', this.dataset.closeLabel || 'Close');

      dialog.append(close, figure);
      document.body.appendChild(dialog);

      this.lightbox = dialog;
      this.lightboxImages = Array.from(figure.querySelectorAll('img'));
      this.lightboxIndex = this.index;

      a11y.lockScroll();
      a11y.trapFocus(dialog, close);

      requestAnimationFrame(() => dialog.classList.add('is-open'));

      close.addEventListener('click', () => this.closeLightbox());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target === figure) this.closeLightbox();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
        if (event.key === 'ArrowRight') this.stepLightbox(1);
      });
    }

    stepLightbox(direction) {
      if (!this.lightboxImages || this.lightboxImages.length === 0) return;
      const total = this.lightboxImages.length;
      const next = (this.lightboxIndex + direction + total) % total;

      this.lightboxImages[this.lightboxIndex].classList.remove('is-active');
      this.lightboxImages[next].classList.add('is-active');
      this.lightboxIndex = next;

      // Keep the page gallery in step so closing the lightbox is not jarring.
      this.select(next, { scroll: true });
    }

    closeLightbox() {
      if (!this.lightbox) return;
      const dialog = this.lightbox;
      this.lightbox = null;

      dialog.classList.remove('is-open');
      a11y.unlockScroll();
      a11y.releaseFocus(true);

      motion.afterTransition(dialog).then(() => dialog.remove());
    }
  }

  window.Aurelia.define('product-gallery', ProductGallery);

  /* =====================================================================
     Optional sub-components — deferred
     Both are merchant-optional and neither is part of the first paint, so
     they load only when the markup is actually on the page.
     ===================================================================== */
  if (document.querySelector('sticky-atc')) {
    window.Aurelia.load('sticky-atc').catch(() => {});
  }
  if (document.querySelector('share-buttons')) {
    window.Aurelia.load('share').catch(() => {});
  }
})();
